import { useEffect, useState } from 'react';
import { Box, Flex, Image } from '@chakra-ui/react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { AlarmClockIcon, CalendarIcon } from 'Asset/icon';
import { Condition, Iteration } from 'Component/others';
import RetryButton from 'Component/retry-button';
import SliderButton from 'Component/slider-button';
import { useUserInfo } from 'Store/userInfo';
import ButtonSolid from 'UiKit/button/buttonSolid';
import InputTextAreaComponent from 'UiKit/input-textarea';
import { TextBold, TextMedium, TextRegular } from 'UiKit/typography';
import usePostStartStream from 'Domain/host-stream/usePostStartStream';
import usePostEndStream from 'Domain/host-stream/usePostEndStream';
import useGetGiveawaySessions from 'Domain/productivity-host/useGetGiveawaySessions';
import { useHostStreamState } from 'View/host-stream/state';
import LoadingState from 'View/host-stream/components/loading-state';

import styles from './styles.module.css';

const StreamForm = ({ isClockedIn, isLivestreaming }) => {
  const { t } = useTranslation('host-stream');
  const { name } = useUserInfo();
  const [note, setNote] = useState('');

  const { state, updateState } = useHostStreamState();
  const { mutate: postStartStream, isPending: isLoadingStartStream } = usePostStartStream();
  const { mutate: postEndStream, isPending: isLoadingEndStream } = usePostEndStream();
  const { data: giveawaySessions } = useGetGiveawaySessions(isLivestreaming);

  const handleNotesChange = (e) => {
    const value = e.target.value
      .trimStart()
      .replace('  ', ' ')
      .replace(/[^a-zA-Z0-9 ]/g, '');
    setNote(value);
  };

  const handleSubmit = async () => {
    if (isLoadingStartStream || isLoadingEndStream) return;

    const payload = {
      selfieImage: state?.capturedImage?.first,
      locationImage: state?.capturedImage?.second,
      note: note || null,
    };

    isLivestreaming ? postEndStream(payload) : postStartStream(payload);
  };

  useEffect(() => {
    updateState({ recordDateTime: new Date() });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box className={styles['stream-form__container']}>
      <Box className={styles['stream-form__content']}>
        <Box className={styles['stream-form__text']}>
          <TextBold className={styles['stream-form__title']}>{name}</TextBold>
          <Flex gap="16px">
            <Flex gap="8px">
              <CalendarIcon size={14} color="var(--color-neutral30)" />
              <TextMedium className={styles['stream-form__subtitle']}>{moment(state?.recordDateTime).format('DD MMM YYYY')}</TextMedium>
            </Flex>
            <Flex gap="8px">
              <AlarmClockIcon size={14} color="var(--color-neutral30)" />
              <TextMedium className={styles['stream-form__subtitle']}>{moment(state?.recordDateTime).format('HH:mm')}</TextMedium>
            </Flex>
          </Flex>
        </Box>
        <Flex gap="20px">
          <Condition statement={state?.capturedImage?.first}>
            <Flex flexDir="column" align="center" gap="8px">
              <TextBold fontSize="var(--font-body2)">{t('preview_page.photo_1_label')}</TextBold>
              <Image src={state?.capturedImage?.first} alt="captured-selfie" className={styles['stream-form__preview-image']} />
              <RetryButton onClick={() => updateState({ currentStep: 0, capturedImage: { first: null, second: state?.capturedImage?.second } })} />
            </Flex>
          </Condition>
          <Condition statement={state?.capturedImage?.second}>
            <Flex flexDir="column" align="center" gap="8px">
              <TextBold fontSize="var(--font-body2)">{t('preview_page.photo_2_label')}</TextBold>
              <Image src={state?.capturedImage?.second} alt="captured-environment" className={styles['stream-form__preview-image']} />
              <RetryButton onClick={() => updateState({ currentStep: 1, capturedImage: { first: state?.capturedImage?.first, second: null } })} />
            </Flex>
          </Condition>
        </Flex>
        <Box className={styles['form-textarea__container']}>
          <Flex className={styles['form-textarea__container--head']}>
            <Flex alignItems="center">
              <TextBold className={styles['form-textarea__label']}>{t('preview_page.notes')}</TextBold>
              <TextRegular className={styles['form-textarea__label--optional']}>{t('preview_page.optional')}</TextRegular>
            </Flex>
            <TextRegular className={styles['form-textarea__count']}>({note?.length}/100)</TextRegular>
          </Flex>
          <InputTextAreaComponent
            value={note}
            className={styles['form-textarea__textarea']}
            placeholder={t('preview_page.note_example')}
            onChange={handleNotesChange}
            maxLength={100}
          />
        </Box>

        <Condition statement={isLivestreaming && giveawaySessions?.length > 0}>
          <Box className={styles['preview-updated-giveaways__container']}>
            <TextBold className={styles['update-giveaway__label']}>{t('update_giveaway.title')}</TextBold>
            <Box className={styles['preview-updated-giveaways__container--list']}>
              <Iteration
                items={giveawaySessions}
                render={(giveawaySession, index) => (
                  <Box key={index} className={styles['preview-updated-giveaways__container--item']}>
                    <TextRegular>{giveawaySession?.productName}</TextRegular>
                    <Box className={styles['updated-stock-badge__container']}>
                      <TextRegular fontSize="var(--font-caption1)" className={styles['updated-stock-badge__label']}>
                        {t('update_giveaway.used')}
                      </TextRegular>
                      <TextRegular fontSize="var(--font-caption1)" className={styles['updated-stock-badge__total']}>
                        <span className={styles['updated-stock-badge__total--used']}>{giveawaySession?.usedStock}/</span>
                        {giveawaySession?.allocatedStock}
                      </TextRegular>
                    </Box>
                  </Box>
                )}
              />
            </Box>
          </Box>
        </Condition>
      </Box>
      <Box className={styles['stream-form__footer']}>
        <Condition
          statement={isLivestreaming}
          fallback={
            <SliderButton
              label={t('preview_page.slide_start_livestream')}
              disabled={!state?.capturedImage?.first || !state?.capturedImage?.second || !isClockedIn}
              onSlide={handleSubmit}
            />
          }
        >
          <ButtonSolid
            background="var(--color-primary70)"
            className={styles['stream-form__button']}
            disabled={!state?.capturedImage?.first || !state?.capturedImage?.second || !isClockedIn}
            onClick={handleSubmit}
          >
            {t('common:submit')}
          </ButtonSolid>
        </Condition>
      </Box>
      <Condition statement={isLoadingStartStream || isLoadingEndStream}>
        <LoadingState />
      </Condition>
    </Box>
  );
};

export default StreamForm;
